import { useOpenUrl } from "../../../../hooks/useOpenUrl";
import { CloseTabButton } from "../../../CloseTabButton";
import { AiHelpBrowser } from "../../../Help/AiHelpBrowser";
import { QuestionDisplay } from "../../../Questions/QuestionDisplay"; // Fix import path
import { PlaybookScriptDisplay } from "../../../Script/PlaybookScriptDisplay";
import { TranscriptTable } from "../../../Transcript/TranscriptTable";
import { useCallContext } from "../../CallContext";

export const CallMainContent = () => {
  const { activeMode, setActiveMode, roomId, roomBot } = useCallContext();
  const openUrl = useOpenUrl();

  const hasCallEnded =
    roomBot?.status_changes?.some((status: { code: string }) => {
      // Call has ended if bot has reached any of these final states
      // NOTE: "recording_done" is NOT a call completion status - it just means
      // a recording segment finished, but the call may still be ongoing
      const endedCallStatuses = [
        "call_ended", // Primary signal - call actually ended
        "done", // Bot completed all tasks
        "analysis_done", // Analysis complete
        "fatal", // Call failed
      ];
      return endedCallStatuses.includes(status.code);
    }) ?? false;

  const handleViewCallReport = () => {
    const dashboardUrl = import.meta.env.VITE_DASH_URL;
    if (dashboardUrl) {
      // Navigate to the specific call analysis page
      const callAnalysisUrl = `${dashboardUrl}/calls/${roomId}`;
      openUrl(callAnalysisUrl);
    } else {
      console.error("Dashboard URL not configured");
      // Fallback to local report mode if dashboard URL not available
      setActiveMode("report");
    }
  };

  const handleViewExampleReport = () => {
    const dashboardUrl = import.meta.env.VITE_DASH_URL;
    if (dashboardUrl) {
      const exampleReportUrl = `${dashboardUrl}/example-call-report`;
      openUrl(exampleReportUrl);
    } else {
      console.error("Dashboard URL not configured");
      // Fallback to local report mode if dashboard URL not available
      setActiveMode("report");
    }
  };

  // Extract nested ternary into a separate function for better readability
  const renderCallContent = () => {
    if (hasCallEnded) {
      // Call has ended - show link to report
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-4">Call Completed</h3>
            <p className="text-gray-600 mb-4">
              The call has ended. View the analysis report for insights.
            </p>
            <button
              type="button"
              onClick={handleViewCallReport}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              View Call Report
            </button>
          </div>
        </div>
      );
    }

    // Always show questions regardless of bot status
    return <QuestionDisplay />;
  };

  // const hasRealtimeFlag = useFeatureFlag("realtime-api");
  // const [useRealtimeAPI] = useLocalStorage("use-realtime-api", false);
  // const usingOpenAIRealtimeMode = hasRealtimeFlag && useRealtimeAPI;

  return (
    <div className="flex-grow overflow-y-auto">
      <div className="flex h-full flex-col">
        {/* Script mode view */}
        <div
          className={`${
            activeMode === "script" ? "block" : "hidden"
          } flex min-h-0 flex-1 flex-grow flex-col`}
        >
          <div className="min-h-0 flex-1">
            <PlaybookScriptDisplay />
          </div>
          <div className="min-h-0 flex-1 flex-grow">{renderCallContent()}</div>
        </div>

        {/* AI help view (Q&A/FAQ functionality) */}
        <div className={`${activeMode === "ai" ? "block" : "hidden"}`}>
          <AiHelpBrowser />
        </div>

        {/* Transcript view */}
        <div
          className={`${
            activeMode === "transcript" ? "block" : "hidden"
          } flex h-full flex-grow flex-col`}
        >
          <TranscriptTable />
        </div>

        {/* Report mode view (post-call analysis) */}
        <div
          className={`${activeMode === "report" ? "block" : "hidden"} relative`}
        >
          <div className="absolute right-2 top-2">
            <CloseTabButton />
          </div>
          <div className="flex h-full flex-col space-y-8">
            <div className="pb-8">
              {activeMode === "report" && (
                <div className="p-8">
                  <div className="text-center mb-8">
                    <h2 className="text-xl font-semibold mb-4">
                      Call Analysis Available
                    </h2>
                    <p className="text-gray-600 mb-4">
                      Call analysis and reports are now available in the
                      Frontier AI Dashboard.
                    </p>
                    <div className="flex justify-center gap-3">
                      <button
                        type="button"
                        onClick={handleViewCallReport}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Open Dashboard
                      </button>
                      <button
                        type="button"
                        onClick={handleViewExampleReport}
                        className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        See Example
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
